import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCESSORIES } from '../utils/constants';
import { selectCurrentVehicle } from '../../../core/reducers/currentVehicle';
import { selectSelectedSubCategory, setSelectedSubCategory } from '../../../core/reducers/ui';
import { selectTransmissionFromCurrentVehicle } from '../../../core/reducers/vehicles';
import { useAccessoriesFetch } from '../../../core/apiHooks/useAccessoriesFetch';
import { useAppSelector } from '../../../core/utils';
import { useFetchCurrentVehiclePayment } from '../../../core/apiHooks/useFetchPayment';
const useAccessoriesData = () => {
    var _a, _b, _c, _d;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const subCategoryLabels = useMemo(() => ({
        ALL_ACCESSORIES: t('Pages.Bap.allAccessoriesLabel'),
        MY_SELECTIONS: t('Pages.Bap.mySelectionsLabel'),
        FEATURED_ACCESSORIES: t('Pages.Bap.featuredLabel'),
    }), [t]);
    const subCategoryGtmNames = new Map();
    subCategoryGtmNames.set(subCategoryLabels.ALL_ACCESSORIES, 'All Accessories');
    subCategoryGtmNames.set(subCategoryLabels.MY_SELECTIONS, 'My Selections');
    subCategoryGtmNames.set(subCategoryLabels.FEATURED_ACCESSORIES, 'Featured');
    const { accessoryKeys } = useAppSelector(selectCurrentVehicle) || {};
    const currentTransmission = useAppSelector(selectTransmissionFromCurrentVehicle);
    const { data: accessoryData, isLoading: isAccessoryDataLoading, isSuccess: isAccessoryDataSuccess, isFetching: isAccessoryDataFetching, error: accessoryDataError, } = useAccessoriesFetch((currentTransmission === null || currentTransmission === void 0 ? void 0 : currentTransmission.detIdentifier) || '');
    const { data: paymentData, isLoading: isPaymentDataLoading, isFetching: isPaymentDataFetching, isSuccess: isPaymentDataSuccess, error: paymentError, } = useFetchCurrentVehiclePayment();
    const isAccessoriesPaymentFailed = (!isPaymentDataLoading && !isPaymentDataFetching && isPaymentDataSuccess && !paymentData) || !!paymentError;
    const isSelected = useMemo(() => (accessory) => accessoryKeys === null || accessoryKeys === void 0 ? void 0 : accessoryKeys.includes(accessory.businessKey), [accessoryKeys]);
    const allAccessories = useMemo(() => (accessoryData === null || accessoryData === void 0 ? void 0 : accessoryData.map(item => {
        var _a;
        return ({
            ...item,
            isSelected: isSelected(item),
            payment: (_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.accessories.find(accessory => accessory.key === item.businessKey)) === null || _a === void 0 ? void 0 : _a.frequencyPayment,
        });
    })) || [], [accessoryData, paymentData, isSelected]);
    const getAccessoriesByCategory = useMemo(() => (category) => allAccessories.filter(item => { var _a; return ((_a = item === null || item === void 0 ? void 0 : item.category) === null || _a === void 0 ? void 0 : _a.id) === category; }), [allAccessories]);
    const groupedByAccessorySubCategory = (_a = getAccessoriesByCategory(ACCESSORIES)) === null || _a === void 0 ? void 0 : _a.reduce((acc, item) => {
        const { name, displayOrder } = item.subCategory || {};
        if (!name)
            return acc;
        if (!acc[name]) {
            acc[name] = {
                items: [],
                displayOrder,
            };
        }
        acc[name].items.push(item);
        return acc;
    }, {});
    const sortedKeys = groupedByAccessorySubCategory &&
        ((_b = Object.keys(groupedByAccessorySubCategory)) === null || _b === void 0 ? void 0 : _b.sort((a, b) => groupedByAccessorySubCategory[a].displayOrder - groupedByAccessorySubCategory[b].displayOrder));
    const orderedAccessories = sortedKeys === null || sortedKeys === void 0 ? void 0 : sortedKeys.reduce((acc, key) => {
        acc[key] = groupedByAccessorySubCategory && groupedByAccessorySubCategory[key].items;
        return acc;
    }, {});
    const featuredAccessories = getAccessoriesByCategory(ACCESSORIES).filter(accessory => accessory.isFeatured);
    const accessoriesBySubCategoryMap = {
        [subCategoryLabels.FEATURED_ACCESSORIES]: featuredAccessories || [],
        [subCategoryLabels.ALL_ACCESSORIES]: getAccessoriesByCategory(ACCESSORIES) || [],
        ...orderedAccessories,
        [subCategoryLabels.MY_SELECTIONS]: (_c = getAccessoriesByCategory(ACCESSORIES)) === null || _c === void 0 ? void 0 : _c.filter(accessory => isSelected(accessory)),
    };
    const getSubCategoryGtmName = (key, value) => {
        // Returns mapped 'en' gtm values
        const subCategoryMap = {
            [subCategoryLabels.ALL_ACCESSORIES]: subCategoryGtmNames.get(subCategoryLabels.ALL_ACCESSORIES),
            [subCategoryLabels.MY_SELECTIONS]: subCategoryGtmNames.get(subCategoryLabels.MY_SELECTIONS),
            [subCategoryLabels.FEATURED_ACCESSORIES]: subCategoryGtmNames.get(subCategoryLabels.FEATURED_ACCESSORIES),
        };
        return subCategoryMap[key] || value;
    };
    const categoryOptions = Object.entries(accessoriesBySubCategoryMap)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => (value === null || value === void 0 ? void 0 : value.length) > 0)
        .map(([key, value]) => {
        var _a, _b;
        return ({
            text: `${key} (${value === null || value === void 0 ? void 0 : value.length})`,
            value: key,
            gtmName: getSubCategoryGtmName(key, (_b = (_a = value === null || value === void 0 ? void 0 : value[0]) === null || _a === void 0 ? void 0 : _a.subCategory) === null || _b === void 0 ? void 0 : _b.gtmName),
            isEmphasized: key === subCategoryLabels.MY_SELECTIONS,
        });
    });
    const defaultSubCategory = useMemo(() => {
        if (featuredAccessories && (featuredAccessories === null || featuredAccessories === void 0 ? void 0 : featuredAccessories.length)) {
            return subCategoryLabels.FEATURED_ACCESSORIES;
        }
        return subCategoryLabels.ALL_ACCESSORIES;
    }, [featuredAccessories, subCategoryLabels.ALL_ACCESSORIES, subCategoryLabels.FEATURED_ACCESSORIES]);
    const selectedSubCategory = ((_d = useAppSelector(selectSelectedSubCategory)) === null || _d === void 0 ? void 0 : _d.accessories) || '';
    function handleSetSelectedSubCategory(subCategory) {
        dispatch(setSelectedSubCategory({ accessories: subCategory }));
    }
    useEffect(() => {
        if (!selectedSubCategory && isAccessoryDataSuccess) {
            handleSetSelectedSubCategory(defaultSubCategory);
        }
    }, [defaultSubCategory, isAccessoryDataSuccess, selectedSubCategory]);
    return {
        accessories: allAccessories,
        isAccessoryDataLoaded: !isAccessoryDataLoading && !isAccessoryDataFetching && isAccessoryDataSuccess,
        isAccessoryDataFailed: !!accessoryDataError,
        isAccessoriesPaymentFailed,
        categoryOptions,
        selectedSubCategory,
        setSelectedSubCategory: handleSetSelectedSubCategory,
        defaultSubCategory,
        subcategoryLabels: subCategoryLabels,
    };
};
export default useAccessoriesData;
